import { useState, useEffect } from 'react'

import { getGif } from '../../gifs/services'
import { RecommendationItem } from '../../recommendations/components'
import type { Gif } from '../../gifs/types'
import type { RecommendationLogEntry } from '../services'

import { DocumentPreviewModal } from './DocumentPreviewModal'

interface LogRecommendationVisualizerProps {
  recommendations: RecommendationLogEntry['results']['recommendations']
}

export function LogRecommendationVisualizer({
  recommendations,
}: LogRecommendationVisualizerProps) {
  const [gifs, setGifs] = useState<Record<string, Gif | null>>({})
  const [loadingGifs, setLoadingGifs] = useState<Record<string, boolean>>({})

  useEffect(() => {
    const fetchGifs = async () => {
      const gifIds = recommendations.map((r) => r.gifId)
      const newLoadingGifs: Record<string, boolean> = {}

      // Set loading state for all GIFs
      gifIds.forEach((id) => {
        newLoadingGifs[id] = true
      })
      setLoadingGifs(newLoadingGifs)

      // Fetch all GIFs
      const gifPromises = gifIds.map(async (gifId) => {
        try {
          const gif = await getGif(gifId)
          return { gifId, gif }
        } catch (error) {
          console.error(`Error fetching GIF ${gifId}:`, error)
          return { gifId, gif: null }
        }
      })

      const gifResults = await Promise.all(gifPromises)

      // Update state with results
      const newGifs: Record<string, Gif | null> = {}
      const newLoadingGifsUpdated: Record<string, boolean> = {}

      gifResults.forEach(({ gifId, gif }) => {
        newGifs[gifId] = gif
        newLoadingGifsUpdated[gifId] = false
      })

      setGifs(newGifs)
      setLoadingGifs(newLoadingGifsUpdated)
    }

    if (recommendations.length > 0) {
      fetchGifs()
    }
  }, [recommendations])

  if (recommendations.length === 0) {
    return (
      <div className="text-center text-default-500 py-4">
        No recommendations to display
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {recommendations.map((recommendation) => {
        const isLoading = loadingGifs[recommendation.gifId] || false
        const gif = gifs[recommendation.gifId] || null

        if (isLoading || !gif) {
          return (
            <RecommendationItem
              key={recommendation.gifId}
              recommendation={recommendation}
              gif={gif}
              isLoading={isLoading}
            />
          )
        }

        return (
          <DocumentPreviewModal
            key={recommendation.gifId}
            gif={gif}
            className="cursor-pointer"
          >
            <RecommendationItem
              recommendation={recommendation}
              gif={gif}
              isLoading={isLoading}
            />
          </DocumentPreviewModal>
        )
      })}
    </div>
  )
}
